import {
  DirectoryTypeConsts,
  DirectoryTypeKeys,
} from 'client/util/Constants/DirectoryConstants';
import { find, values } from 'lodash';
import { parallel } from 'async';
import CollectionApi from 'client/api/v2/CollectionApi';
import LdapServerApi from 'client/api/v2/LdapServerApi';

export class DirectoryApiClass extends CollectionApi {
  constructor() {
    super('/api/v2/directories', {
      limit: 50,
      skip: 0,
      sort: 'name', // Prepend with a '-' to reverse default sort direction
    });
  }

  /**
   * Override method from base class implement directory specific business
   * logic.
   */
  fetch(skip, limit, sort, searchTerm, cancelToken, filters = []) {
    return super
      .fetch(skip, limit)
      .then((response) => {
        // If the activated filter is present then make requests to check if
        // LDAP is activated.
        const activatedFilter = find(filters, filter => filter.name === 'activated');

        if (activatedFilter) {
          const ldap = find(
            response.results,
            result => result.type === DirectoryTypeConsts[DirectoryTypeKeys.ldapServer],
          );

          return DirectoryApiClass
            .checkIfLdapIsActivated(ldap)
            .then((activated) => {
              ldap.activated = activated;
              return response;
            })
            .catch(() => response);
        }

        return response;
      })
      .then((response) => {
        let { results } = response;

        // Process retrieved results for Directory specific business logic
        results = DirectoryApiClass.setActivatedField(results);

        DirectoryApiClass.sortItemsByNameAndType(sort, results);
        DirectoryApiClass.applyFilters(filters, results);

        results = DirectoryApiClass.removeUnknownDirectories(results);

        return { results, totalCount: results.length };
      });
  }

  fetchAll(searchTerm, boundItems, filters = []) {
    return this
      .fetch(0, 20, null, null, null, filters)
      .then(response => response.results);
  }

  getLdap() {
    return this
      .fetch()
      .then(response => find(
        response.results,
        result => result.type === DirectoryTypeConsts[DirectoryTypeKeys.ldapServer],
      ));
  }

  // --------------------------------------------------------------------------
  // Helper Methods for Directory Specific Business Logic
  // --------------------------------------------------------------------------

  /**
   * The /api/v2/directories endpoint does not support filter query string
   * parameters. As a result of this, and because default inactive directories
   * are inserted above in addInactiveDirectories(), that functionality is
   * provided here in client side code.
   */
  static applyFilters(filters = [], results = []) {
    filters.forEach((filter) => {
      const { name, operator } = filter;

      if (filter.value === '') {
        return;
      }

      const filterValues = typeof filter.value === 'string'
        ? filter.value.split('|') : [filter.value];

      for (let i = 0; i < results.length; i += 1) {
        let removeItem = false;
        const result = results[i];
        const resultFound = filterValues.includes(result[name]);

        if (operator === 'eq' && !resultFound) {
          removeItem = true;
        } else if (operator === 'ne' && resultFound) {
          removeItem = true;
        }

        if (removeItem) {
          results.splice(i, 1);
          i -= 1;
        }
      }
    });
  }

  static checkIfLdapIsActivated(ldap) {
    return new Promise((resolve) => {
      const tasks = {
        sambaDomainCount: cb => LdapServerApi
          .getSambaDomainCount(ldap.id)
          .then(sambaDomainCount => cb(null, sambaDomainCount))
          .catch(() => cb(null, 0)),
        ldapBoundUserGroupCount: cb => LdapServerApi
          .getBoundUserGroupCount(ldap.id)
          .then(ldapBoundUserGroupCount => cb(null, ldapBoundUserGroupCount))
          .catch(() => cb(null, 0)),
        ldapBoundUserCount: cb => LdapServerApi
          .getBoundUserCount(ldap.id)
          .then(ldapBoundUserCount => cb(null, ldapBoundUserCount))
          .catch(() => cb(null, 0)),
      };

      parallel(tasks, (error, results) => {
        const {
          sambaDomainCount,
          ldapBoundUserGroupCount,
          ldapBoundUserCount,
        } = results;

        const activated = sambaDomainCount > 0
          || ldapBoundUserGroupCount > 0
          || ldapBoundUserCount > 0;

        resolve(activated);
      });
    });
  }

  /**
   * Remove unknown directory types from the results so that they are not shown
   * to the admin user prematurely until they are supported in the UI.
   */
  static removeUnknownDirectories(results = []) {
    const knownDirectoryTypes = values(DirectoryTypeConsts);
    return results.filter(result => knownDirectoryTypes.indexOf(result.type) !== -1);
  }

  /**
   * Existing directories without an activated field are considered _activated_.
   */
  static setActivatedField(results = []) {
    return results.map(result => ({
      ...result,
      activated: typeof result.activated === 'boolean' ? result.activated : true,
    }));
  }

  /**
   * Sort the results on the client side because the server doesn't support the
   * sort query string parameter and new records may have been inserted above in
   * addInactiveDirectories().
   */
  static sortItemsByNameAndType(sortField, results = []) {
    if (!sortField) {
      return;
    }

    // Sort the items by their name
    results.sort((a, b) => {
      const descending = !!String(sortField).match(/^-/);
      const aName = String(a.name).toLowerCase();
      const bName = String(b.name).toLowerCase();

      if (descending) {
        if (aName > bName) {
          return -1;
        }
        if (aName < bName) {
          return 1;
        }
        return 0;
      }

      // Ascending order by name sort
      if (aName < bName) {
        return -1;
      }
      if (aName > bName) {
        return 1;
      }
      return 0;
    });

    // Now sort the items by their directory type
    // (active_diretory, g_suite, ldap_server, office_365, workday)
    results.sort((a, b) => {
      const aType = String(a.type);
      const bType = String(b.type);
      if (aType > bType) {
        return 1;
      }
      if (aType < bType) {
        return -1;
      }
      return 0;
    });
  }
}

export default new DirectoryApiClass();
